import { Injectable } from "@angular/core";
import { firstValueFrom } from "rxjs";
import type { DispatchGuideOrderNumberType } from "../models/ost/documents/dispatch-guide.model";
import { DocumentsService } from "./documents.service";

export interface IDocumentFile {
  documentKey: string;
  data: Uint8Array;
  mimeType: string;
  lastAccessed: number;
  size: number;
}

@Injectable({ providedIn: "root" })
export class IndexDbDocumentService {
  private dbName = "DocumentStorageDB";
  private storeName = "documents";
  private dbVersion = 1;
  private dbPromise: Promise<IDBDatabase>;

  constructor(private readonly documentService: DocumentsService) {
    this.dbPromise = this.initDB();
  }

  private initDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onupgradeneeded = (): void => {
        const db = request.result;
        if (!db.objectStoreNames.contains(this.storeName)) {
          db.createObjectStore(this.storeName, { keyPath: "documentKey" });
        }
      };

      request.onsuccess = (): void => resolve(request.result);
      request.onerror = (): void => reject("IndexedDB initialization failed");
    });
  }

  async saveDocument(documentKey: string, base64Data: string, mimeType: string): Promise<void> {
    const db = await this.dbPromise;
    const transaction = db.transaction(this.storeName, "readwrite");
    const store = transaction.objectStore(this.storeName);

    const binaryData = this.base64ToUint8Array(base64Data);

    await new Promise<void>((resolve, reject) => {
      const request = store.put({
        documentKey,
        data: binaryData,
        mimeType,
        lastAccessed: Date.now(),
        size: binaryData.byteLength,
      });

      request.onsuccess = (): void => resolve();
      request.onerror = (): void => reject("Failed to save document");
    });
  }

  public async getDispatchGuide(documentKey: string, identifier: string | number = documentKey, orderNumberType: DispatchGuideOrderNumberType = "DISPATCH"): Promise<void> {
    const existingDocument = await this.getDocument(documentKey);
    if (existingDocument) {
      this.openDocumentInNewTab(existingDocument.data, existingDocument.mimeType);
      return;
    }
    const document = await firstValueFrom(this.documentService.getDispatchGuide(identifier, orderNumberType));
    await this.saveDocument(documentKey, document.fileContent, "application/pdf");
    const binaryData = this.base64ToUint8Array(document.fileContent);
    this.openDocumentInNewTab(binaryData, "application/pdf");
  }

  private openDocumentInNewTab(binaryData: Uint8Array, mimeType: string): void {
    const blob = new Blob([binaryData as BlobPart], { type: mimeType });
    const blobUrl = URL.createObjectURL(blob);

    const newTab = window.open(blobUrl, "_blank");
    if (newTab) {
      newTab.onbeforeunload = (): void => URL.revokeObjectURL(blobUrl);
    } else {
      const base64 = this.uint8ArrayToBase64(binaryData);
      const dataUrl = `data:${mimeType};base64,${base64}`;
      window.open(dataUrl, "_blank");
    }
  }

  private base64ToUint8Array(base64: string): Uint8Array {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
  }

  private uint8ArrayToBase64(bytes: Uint8Array): string {
    let binary = "";
    bytes.forEach((byte: number): string => (binary += String.fromCharCode(byte)));
    return btoa(binary);
  }

  async getDocument(documentKey: string): Promise<IDocumentFile | null> {
    const db = await this.dbPromise;
    return new Promise((resolve) => {
      const transaction = db.transaction(this.storeName, "readonly");
      const store = transaction.objectStore(this.storeName);
      const request = store.get(documentKey);

      request.onsuccess = (): void => resolve(request.result as IDocumentFile);
      request.onerror = (): void => resolve(null);
    });
  }
}
